import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './diarioObraDetalhesHome.css';
import FloatingMenu from './FloatingMenu';
import { apiService } from '../../../services/ApiService';
import Header from '../../Header';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import gerarRelatorioDiarioObraPDF from './RelatorioDiarioObraPDF';
import DateCarousel from './DateCarousel';
import { toast } from 'react-hot-toast';
import CadastroClienteModal from './CadastroClienteModal';
import CompartilharObraModal from './CompartilharObraModal';

const DiarioObraDetalhesHome = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const [dadosObra, setDadosObra] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [registros, setRegistros] = useState({});
  const [tiposRegistro] = useState([
    'clima', 'maoDeObra', 'equipamentos', 'atividade', 'materiais', 'gerais', 'ocorrencia', 'info'
  ]);
  const [temRegistros, setTemRegistros] = useState(false);
  const [responseDebug, setResponseDebug] = useState(null);
  const [fotosMaisRecentes, setFotosMaisRecentes] = useState([]);
  const [carregandoFotos, setCarregandoFotos] = useState(false);
  const [carregandoRegistros, setCarregandoRegistros] = useState(false);
  const [dataSelecionada, setDataSelecionada] = useState(new Date().toISOString().split('T')[0]);
  const [registrosFiltrados, setRegistrosFiltrados] = useState({});
  const [isCadastroClienteModalOpen, setIsCadastroClienteModalOpen] = useState(false);
  const [isCompartilharModalOpen, setIsCompartilharModalOpen] = useState(false);
  const [aprovandoDia, setAprovandoDia] = useState(false);
  const [diaAprovado, setDiaAprovado] = useState(false);
  const [dadosAprovacao, setDadosAprovacao] = useState(null);

  // Verificar se o usuário tem permissão para ver os botões de ação
  const canManageObra = currentUser?.roleEmpresa === 'admin' || currentUser?.roleEmpresa === 'manager';
  const isClient = currentUser?.role === 'client';

  // Função para ajustar o fuso horário
  const ajustarFusoHorario = (dateString) => {
    if (!dateString) return new Date(); // Retornar data atual se não tiver data
    try {
      // Quebra a data em ano, mês e dia
      const [ano, mes, dia] = dateString.split('-');
      
      // Cria uma nova data, sem alterar o horário
      const adjustedDate = new Date(ano, mes - 1, dia);  // Meses começam do 0 no JavaScript
      
      // Verificar se a data é válida
      if (isNaN(adjustedDate.getTime())) {
        console.error("Data inválida:", dateString);
        return new Date();  // Fallback para a data atual
      }
      
      return adjustedDate;
    } catch (error) {
      console.error("Erro ao processar data:", error);
      return new Date();  // Fallback para a data atual
    }
  };

  // Função para formatar data no padrão brasileiro, considerando o fuso horário
  const formatarData = (dataString) => {
    if (!dataString) return '-';
    try {
      // Extrair ano, mês e dia da string de data
      const [ano, mes, dia] = dataString.split('-');
      // Criar data com horário fixo no meio do dia (12:00:00) para evitar problemas de fuso horário
      const data = new Date(ano, mes - 1, dia, 12, 0, 0);
      return data.toLocaleDateString('pt-BR');
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return '-';
    }
  };

  // Função para carregar todos os tipos de registros da obra
  const carregarRegistrosObra = async () => {
    if (!id) return;
    
    try {
      setCarregandoRegistros(true);
      
      // Para cada tipo de registro, carregar do servidor
      const todosRegistros = { ...registros };
      let encontrouAlgumRegistro = false;
      
      // Buscar registros de cada tipo
      for (const tipo of tiposRegistro) {
        try {
          const response = await apiService.getDiarioObraMediaList(id, tipo);
          
          let registrosTipo = [];
          
          // Verificar diferentes estruturas possíveis
          if (response.success) {
            // Estrutura 1: response.data[tipo] é um array
            if (response.data && Array.isArray(response.data[tipo])) {
              registrosTipo = response.data[tipo];
            } 
            // Estrutura 2: response.data.data[tipo] é um array
            else if (response.data && response.data.data && Array.isArray(response.data.data[tipo])) {
              registrosTipo = response.data.data[tipo];
            } 
            // Estrutura 3: response.data[tipo] é um objeto com IDs como chaves
            else if (response.data && response.data[tipo] && typeof response.data[tipo] === 'object') {
              registrosTipo = Object.entries(response.data[tipo]).map(([id, dados]) => ({
                id,
                ...dados,
                data: dados.data || new Date().toISOString()
              }));
            }
            // Estrutura 4: response.data.data[tipo] é um objeto com IDs como chaves
            else if (response.data && response.data.data && response.data.data[tipo] && typeof response.data.data[tipo] === 'object') {
              registrosTipo = Object.entries(response.data.data[tipo]).map(([id, dados]) => ({
                id,
                ...dados,
                data: dados.data || new Date().toISOString()
              }));
            }
          }
          
          if (registrosTipo.length > 0) {
            todosRegistros[tipo] = registrosTipo;
            encontrouAlgumRegistro = true;
          }
        } catch (error) {
          console.error(`Erro ao carregar registros do tipo ${tipo}:`, error);
        }
      }
      
      setRegistros(todosRegistros);
      setTemRegistros(encontrouAlgumRegistro);
    } catch (error) {
      console.error('Erro ao carregar registros:', error);
      toast.error('Erro ao carregar registros da obra');
    } finally {
      setCarregandoRegistros(false);
    }
  };

  useEffect(() => {
    const buscarDadosObra = async () => {
      if (!id) {
        setErro("ID da obra não fornecido");
        setCarregando(false);
        return;
      }

      try {
        setCarregando(true);
        setErro(null);
        
        // Buscar os detalhes da obra diretamente
        const resposta = await apiService.getDiarioObraDetalhes(id);
        
        // Salvar a resposta para depuração
        setResponseDebug(resposta);
        
        // Verificar a estrutura da resposta e tentar encontrar os dados da obra
        if (resposta.success) {
          let dadosObraEncontrados = null;
          let registrosEncontrados = {};
          
          // Verificar diferentes possíveis estruturas da resposta
          // Caso de aninhamento duplo: resposta.data.data.obra
          if (resposta.data && resposta.data.data && resposta.data.data.obra) {
            dadosObraEncontrados = resposta.data.data.obra;
            registrosEncontrados = resposta.data.data.registros || {};
          }
          // Caso de aninhamento simples: resposta.data.obra
          else if (resposta.data && resposta.data.obra) {
            dadosObraEncontrados = resposta.data.obra;
            registrosEncontrados = resposta.data.registros || {};
          } else if (resposta.data && resposta.data.id) {
            // Se os dados da obra estiverem diretamente em data
            dadosObraEncontrados = resposta.data;
            registrosEncontrados = resposta.registros || {};
          } else if (resposta.obra) {
            // Se os dados da obra estiverem diretamente na raiz da resposta
            dadosObraEncontrados = resposta.obra;
            registrosEncontrados = resposta.registros || {};
          } else {
            // Tentar criar um objeto de obra a partir do ID
            dadosObraEncontrados = {
              id: id,
              nome: id.split('_').join(' ').toUpperCase(),
              status: 'Em andamento'
            };
          }
          
          if (dadosObraEncontrados) {
            setDadosObra(dadosObraEncontrados);
            
            // Verificar a estrutura dos registros e processá-los adequadamente
            const registrosProcessados = {};
            Object.entries(registrosEncontrados).forEach(([tipo, registros]) => {
              if (registros && typeof registros === 'object') {
                if (Array.isArray(registros)) {
                  registrosProcessados[tipo] = registros;
                } else {
                  const registrosArray = Object.entries(registros).map(([id, dados]) => {
                    return {
                      id,
                      ...dados,
                      data: dados.data || new Date().toISOString()
                    };
                  });
                  registrosProcessados[tipo] = registrosArray;
                }
              }
            });
            
            if (Object.keys(registrosProcessados).length > 0) {
              setRegistros(registrosProcessados);
              const temAlgumRegistro = Object.values(registrosProcessados).some(
                categoria => Array.isArray(categoria) && categoria.length > 0
              );
              setTemRegistros(temAlgumRegistro);
            } else {
              setTemRegistros(false);
              carregarRegistrosObra();
            }
          } else {
            setErro(`Não foi possível carregar os detalhes da obra ${id}`);
          }
        } else {
          setErro(resposta.error || `Obra com ID ${id} não encontrada`);
        }
      } catch (erro) {
        setErro("Ocorreu um erro ao buscar os dados da obra");
      } finally {
        setCarregando(false);
      }
    };

    buscarDadosObra();
  }, [id]);

  useEffect(() => {
    if (!dataSelecionada || !registros || Object.keys(registros).length === 0) {
      setRegistrosFiltrados(registros);
      return;
    }

    try {
      // Filtrar registros pela data selecionada
      const filtrados = Object.keys(registros).reduce((acc, tipo) => {
        if (!registros[tipo] || !Array.isArray(registros[tipo])) {
          acc[tipo] = [];
          return acc;
        }
        
        acc[tipo] = registros[tipo].filter(registro => {
          if (!registro.data) return false;
          
          try {
            // Formatar a data do registro (YYYY-MM-DD) para comparação
            const dataRegistro = new Date(registro.data).toISOString().split('T')[0];
            return dataRegistro === dataSelecionada;
          } catch (error) {
            console.error(`Erro ao processar data do registro tipo ${tipo}:`, error);
            return false;
          }
        });
        
        return acc;
      }, {});
      
      setRegistrosFiltrados(filtrados);
      
      // Atualizar também as fotos mais recentes de acordo com a data selecionada
      if (filtrados.fotos && filtrados.fotos.length > 0) {        
        // Adicionar URLs para as fotos filtradas
        const fotosComUrl = filtrados.fotos.map(foto => {
          try {
            // Priorizar a URL que já foi salva no Storage
            if (foto.arquivo && foto.arquivo.url) {
              return {
                ...foto,
                url: foto.arquivo.url  // URL direta do Storage
              };
            }
            
            // Segundo caso: tem dataUri (imagens convertidas do Realtime Database)
            if (foto.arquivo && foto.arquivo.dataUri) {
              return {
                ...foto,
                url: foto.arquivo.dataUri
              };
            }
            
            // Último caso: usar a API para acessar o arquivo
            return {
              ...foto,
              url: apiService.getDiarioObraMediaFileUrl(id, 'fotos', foto.id)
            };
          } catch (error) {
            console.error("Erro ao processar URL da foto:", error);
            return {
              ...foto,
              url: '/placeholder-image.jpg' // Imagem placeholder em caso de erro
            };
          }
        });
        
        // Ordenar por hora (mais recentes primeiro)
        const fotosOrdenadas = [...fotosComUrl].sort((a, b) => {
          try {
            return new Date(b.data) - new Date(a.data);
          } catch (error) {
            return 0; // Em caso de erro, manter a ordem original
          }
        });
        
        setFotosMaisRecentes(fotosOrdenadas);
      } else {
        setFotosMaisRecentes([]);
      }
    } catch (error) {
      console.error("Erro ao filtrar registros por data:", error);
      setRegistrosFiltrados({});
      setFotosMaisRecentes([]);
    }
  }, [dataSelecionada, registros, id]);

  const atualizarRegistros = (tipo, novoRegistro) => {
    
    // Atualizar o estado local dos registros
    setRegistros(prevRegistros => {
      const novosRegistros = {
        ...prevRegistros,
        [tipo]: [...(prevRegistros[tipo] || []), { ...novoRegistro, id: Date.now(), data: new Date().toISOString() }]
      };
      
      // Atualizar o flag indicando que agora temos registros
      setTemRegistros(true);
      
      // Se o tipo for 'fotos', atualizar também fotosMaisRecentes
      if (tipo === 'fotos') {
        // Recarregar dados da obra para atualizar fotos
        buscarDadosObra();
      }
      
      return novosRegistros;
    });
    
  };

  const voltarParaLista = () => {
    navigate('/diario-obra');
  };

  const handleSelecionarData = (data) => {
    // Garantir que data seja uma string válida
    if (!data) {
      data = new Date().toISOString().split('T')[0];
    }    
    
    try {
      // Atualizar o estado da data selecionada
      setDataSelecionada(data);
      
      // Filtrar registros para a data selecionada
      const dataFormatada = new Date(data).toISOString().split('T')[0];
      
      // Cria uma cópia dos registros para filtrar
      const registrosFiltradosTemp = {};
      
      // Para cada tipo de registro, filtrar apenas os da data selecionada
      Object.keys(registros).forEach(tipo => {
        if (Array.isArray(registros[tipo])) {
          registrosFiltradosTemp[tipo] = registros[tipo].filter(registro => {
            try {
              if (!registro.data) return false;
              const dataRegistro = new Date(registro.data).toISOString().split('T')[0];
              return dataRegistro === dataFormatada;
            } catch (err) {
              console.error(`Erro ao filtrar registro do tipo ${tipo}:`, err);
              return false;
            }
          });
        }
      });
      
      // Atualizar o estado com os registros filtrados
      setRegistrosFiltrados(registrosFiltradosTemp);
    } catch (error) {
      console.error("Erro ao selecionar data:", error);
      toast.error("Erro ao processar data selecionada. Tente novamente.");
    }
  };

  const handleGerarPDF = async () => {
    setCarregando(true);
    try {
      await gerarRelatorioDiarioObraPDF({
        ...dadosObra,
        dadosRelatorio: registrosFiltrados || registros,
        dataSelecionada,
        // Adicionar explicitamente os campos principais para facilitar acesso no PDF
        dataInicioObra: dadosObra?.dataInicio || dadosObra?.dados?.dataInicio || dadosObra?.cronograma?.dataInicio,
        dataFimObra: dadosObra?.dataPrevisaoFim || dadosObra?.dados?.dataPrevisaoFim || dadosObra?.cronograma?.dataPrevisaoFim,
        localObra: dadosObra?.endereco || dadosObra?.dados?.endereco || dadosObra?.localizacao?.endereco,
        contratante: dadosObra?.cliente || dadosObra?.dados?.cliente || dadosObra?.dadosGerais?.cliente,
        responsavelTecnico: dadosObra?.responsavel || dadosObra?.engenheiroResponsavel || dadosObra?.dados?.responsavel
      });
      toast.success("Relatório gerado com sucesso!");
    } catch (erro) {
      console.error("Erro ao gerar relatório:", erro);
      toast.error("Erro ao gerar o relatório. Tente novamente.");
    } finally {
      setCarregando(false);
    }
  };

  // Adicionar useEffect para verificar aprovação quando a data muda
  useEffect(() => {
    if (id && dataSelecionada) {
      verificarAprovacaoDia();
    }
  }, [id, dataSelecionada]);

  // Função para verificar se o dia selecionado está aprovado
  const verificarAprovacaoDia = async () => {
    try {
      const response = await apiService.verificarAprovacaoRDO(id, dataSelecionada);
      
      if (response.success) {
        setDiaAprovado(response.aprovado);
        if (response.aprovado && response.dados) {
          setDadosAprovacao(response.dados);
        } else {
          setDadosAprovacao(null);
        }
      } else {
        console.error("Erro ao verificar aprovação:", response.error);
        setDiaAprovado(false);
        setDadosAprovacao(null);
      }
    } catch (error) {
      console.error("Erro ao verificar aprovação:", error);
      setDiaAprovado(false);
      setDadosAprovacao(null);
    }
  };

  // Função para aprovar o dia selecionado
  const aprovarDia = async () => {
    try {
      setAprovandoDia(true);
      
      const observacoes = prompt("Observações sobre a aprovação (opcional):");
      
      const response = await apiService.aprovarDiaRDO(id, dataSelecionada, observacoes || '');
      
      if (response.success) {
        toast.success("Dia aprovado com sucesso!");
        setDiaAprovado(true);
        setDadosAprovacao(response.dados);
      } else {
        toast.error(`Erro ao aprovar dia: ${response.error || 'Tente novamente.'}`);
      }
    } catch (error) {
      console.error("Erro ao aprovar dia:", error);
      toast.error("Erro ao aprovar dia. Tente novamente.");
    } finally {
      setAprovandoDia(false);
    }
  };

  // Filtrar registros quando os dados são carregados ou a data selecionada muda
  useEffect(() => {
    if (Object.keys(registros).length > 0 && dataSelecionada) {
      handleSelecionarData(dataSelecionada);
    }
  }, [registros, dataSelecionada]);

  if (carregando) {
    return (
      <div className={`carregando-container ${darkMode ? 'dark-mode' : ''}`}>
        <div className="loader"></div>
        <p>Carregando detalhes da obra...</p>
      </div>
    );
  }

  return (
    <>
      <Header />
    
      <div className={`diario-obra-detalhes-container ${darkMode ? 'dark-mode' : ''}`}>
        <div className="voltar-container">
          <button className="btn-voltar" onClick={voltarParaLista}>
            <i className="fas fa-arrow-left"></i> Voltar para lista de obras
          </button>
          <div className="botoes-container">
            {/* Botões de aprovação no topo */}
            {(dadosObra?.compartilhada || canManageObra) && (
              <div className={`aprovacao-container-top ${diaAprovado ? 'aprovado' : ''}`}>
                {diaAprovado ? (
                  <button 
                    className="btn-aprovado"
                    title={`Aprovado por ${dadosAprovacao?.nomeUsuario || 'um usuário'} em ${
                      dadosAprovacao?.dataAprovacao ? new Date(dadosAprovacao.dataAprovacao).toLocaleDateString('pt-BR') : '-'
                    }${dadosAprovacao?.observacoes ? ` - ${dadosAprovacao.observacoes}` : ''}`}
                    disabled={true}
                  >
                    <i className="fas fa-check-circle"></i> Aprovado
                  </button>
                ) : (
                  <button 
                    className={`btn-aprovar-top ${aprovandoDia ? 'aprovando' : ''}`}
                    onClick={aprovarDia}
                    disabled={aprovandoDia}
                  >
                    {aprovandoDia ? (
                      <>
                        <div className="spinner"></div>
                        <span>Aprovando...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check"></i>
                        <span>Aprovar RDO</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
            
            <button 
              className="btn-gerar-pdf" 
              onClick={handleGerarPDF}
              disabled={carregando}
            >
              {carregando ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Gerando PDF...
                </>
              ) : (
                <>
                  <i className="fas fa-file-pdf"></i> Gerar Relatório PDF
                </>
              )}
            </button>
            {canManageObra && (
              <>
                <button className="btn-cadastrar-cliente" onClick={() => setIsCadastroClienteModalOpen(true)}>
                  <i className="fas fa-user-plus"></i> Cadastrar Cliente
                </button>
                <button 
                  className="btn-compartilhar"
                  onClick={() => setIsCompartilharModalOpen(true)}
                >
                  <i className="fas fa-share-alt"></i> Compartilhar Obra
                </button>
              </>
            )}
          </div>
        </div>
        
        <div className="diario-obra-cabecalho">
          <h1>{dadosObra.nome || 'Obra sem nome'}</h1>
          <div className="diario-obra-status">
            <span className={`status-badge ${dadosObra.status?.toLowerCase().replace(' ', '-') || 'em-andamento'}`}>
              {dadosObra.status || 'Em andamento'}
            </span>
          </div>
        </div>

        {dadosObra && (
          <>
            <DateCarousel 
              dataInicial={dadosObra.dataInicio || dadosObra.dados?.dataInicio || new Date().toISOString()}
              dataFinal={dadosObra.dataPrevisaoFim || dadosObra.dados?.dataPrevisaoFim || new Date().toISOString()}
              dataSelecionada={dataSelecionada}
              onDateSelect={handleSelecionarData}
              darkMode={darkMode}
            />
  
          </>
        )}

        <div className="diario-obra-info-cards">
          <div className="info-card">
            <h3>Dados Gerais</h3>
            <div className="info-item">
              <label>Cliente:</label>
              <span>
                {(() => {
                  // Tentar diferentes caminhos para encontrar o valor do cliente
                  const cliente = dadosObra.cliente || 
                                  dadosObra.dados?.cliente ||
                                  (dadosObra.dadosGerais?.cliente) ||
                                  (dadosObra.informacoes?.cliente);
                  return cliente || 'Não definido';
                })()}
              </span>
            </div>
            <div className="info-item">
              <label>Endereço:</label>
              <span>
                {(() => {
                  // Tentar diferentes caminhos para encontrar o endereço
                  const endereco = dadosObra.endereco || 
                                   dadosObra.dados?.endereco ||
                                   dadosObra.dadosGerais?.endereco ||
                                   dadosObra.localizacao?.endereco ||
                                   dadosObra.informacoes?.endereco;
                  return endereco || 'Não definido';
                })()}
              </span>
            </div>
            <div className="info-item">
              <label>Engenheiro:</label>
              <span>
                {(() => {
                  // Tentar diferentes caminhos para encontrar o responsável
                  const responsavel = dadosObra.responsavel || 
                                      dadosObra.engenheiroResponsavel ||
                                      dadosObra.dados?.responsavel || 
                                      dadosObra.dados?.engenheiroResponsavel ||
                                      dadosObra.dadosGerais?.responsavel ||
                                      dadosObra.equipe?.responsavel;
                  return responsavel || 'Não definido';
                })()}
              </span>
            </div>
            {(() => {
              // Tentar diferentes caminhos para encontrar o telefone
              const telefone = dadosObra.telefone || 
                               dadosObra.dados?.telefone ||
                               dadosObra.contato?.telefone || 
                               dadosObra.dadosGerais?.telefone;
              return telefone ? (
                <div className="info-item">
                  <label>Telefone:</label>
                  <span>{telefone}</span>
                </div>
              ) : null;
            })()}
            {(() => {
              // Tentar diferentes caminhos para encontrar o email de contato
              const email = dadosObra.email || 
                            dadosObra.dados?.email ||
                            dadosObra.contato?.email || 
                            dadosObra.dadosGerais?.email;
              return email ? (
                <div className="info-item">
                  <label>Email:</label>
                  <span>{email}</span>
                </div>
              ) : null;
            })()}
          </div>

          <div className="info-card">
            <h3>Cronograma</h3>
            <div className="info-item">
              <label>Início:</label>
              <span>
                {(() => {
                  // Tentar diferentes caminhos para encontrar a data de início
                  const dataInicio = dadosObra.dataInicio || 
                                    dadosObra.dados?.dataInicio ||
                                    dadosObra.cronograma?.dataInicio ||
                                    dadosObra.datas?.inicio;
                  if (!dataInicio) return 'Não definido';
                  
                  // Ajustar a data para o fuso horário local
                  const data = new Date(dataInicio);
                  // Adicionar um dia para compensar o fuso horário UTC
                  data.setDate(data.getDate() + 1);
                  return data.toLocaleDateString('pt-BR');
                })()}
              </span>
            </div>
            <div className="info-item">
              <label>Previsão de Término:</label>
              <span>
                {(() => {
                  // Tentar diferentes caminhos para encontrar a data de término
                  const dataFim = dadosObra.dataPrevisaoFim || 
                                 dadosObra.dados?.dataPrevisaoFim ||
                                 dadosObra.cronograma?.dataPrevisaoFim ||
                                 dadosObra.datas?.previsaoFim;
                  if (!dataFim) return 'Não definido';
                  
                  // Ajustar a data para o fuso horário local
                  const data = new Date(dataFim);
                  // Adicionar um dia para compensar o fuso horário UTC
                  data.setDate(data.getDate() + 1);
                  return data.toLocaleDateString('pt-BR');
                })()}
              </span>
            </div>
            <div className="progresso-container">
              <label>Progresso:</label>
              <div className="barra-progresso">
                <div 
                  className="progresso-preenchido" 
                  style={{ width: `${(() => {
                    // Tentar diferentes caminhos para encontrar o progresso
                    const progresso = dadosObra.progresso || 
                                     dadosObra.dados?.progresso ||
                                     dadosObra.cronograma?.progresso ||
                                     dadosObra.status?.progresso;
                    return progresso || 0;
                  })()}%` }}
                />
                <span className="progresso-texto">
                  {(() => {
                    // Tentar diferentes caminhos para encontrar o progresso
                    const progresso = dadosObra.progresso || 
                                     dadosObra.dados?.progresso ||
                                     dadosObra.cronograma?.progresso ||
                                     dadosObra.status?.progresso;
                    return progresso || 0;
                  })()}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Área para exibir os registros ou mensagem quando não houver nenhum */}
        <div className="registros-container">
          <div className="registros-header">
            <h2>
              {dataSelecionada ? (
                <>Registros da Obra - {ajustarFusoHorario(dataSelecionada).toLocaleDateString('pt-BR')}</>
              ) : (
                <>Registros da Obra</>
              )}
            </h2>
          </div>

          {carregandoRegistros && (
            <div className="carregando-registros">
              <div className="spinner"></div>
              <p>Carregando registros da obra...</p>
            </div>
          )}
          
          {!carregandoRegistros && !temRegistros && (
            <div className="sem-registros">
              <div className="sem-registros-icon">
                <i className="fas fa-clipboard"></i>
              </div>
              <h3>Nenhum registro encontrado</h3>
              <p>Clique no botão <i className="fas fa-plus"></i> para adicionar um novo registro neste diário de obra.</p>
            </div>
          )}

          {!carregandoRegistros && temRegistros && (
              <div className="registros-lista">
              {/* Seção de informações gerais */}
              {registrosFiltrados.info && registrosFiltrados.info.length > 0 && (
                <div className="secao-registros">
                  <h3>
                    <i className="fas fa-info-circle"></i> Informações Gerais
                    <span className="contador">{registrosFiltrados.info.length}</span>
                  </h3>
                  <div className="cards-grid">
                    {registrosFiltrados.info.map(registro => (
                      <div className="registro-card" key={registro.id}>
                        <div className="registro-header">
                          <span className="registro-data">
                            {new Date(registro.data).toLocaleDateString('pt-BR')}
                          </span>
                          <span className="registro-hora">
                            {new Date(registro.data).toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'})}
                          </span>
                        </div>
                        <div className="registro-conteudo">
                          {registro.dados.titulo && <p><strong>Título:</strong> {registro.dados.titulo}</p>}
                          {registro.dados.descricao && <p><strong>Descrição:</strong> {registro.dados.descricao}</p>}
                          {registro.dados.observacoes && <p><strong>Observações:</strong> {registro.dados.observacoes}</p>}
                          {registro.dados.numeroOcorrencia && <p><strong>Número da Ocorrência:</strong> {registro.dados.numeroOcorrencia}</p>}
                          {registro.dados.rdo && <p><strong>RDO:</strong> {registro.dados.rdo}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Seção de fotos recentes - alterar para exibir fotos da data selecionada */}
              {fotosMaisRecentes.length > 0 && (
                <div className="secao-registros">
                  <h3>
                    <i className="fas fa-camera"></i> 
                    {dataSelecionada ? 'Fotos do Dia' : 'Fotos Recentes'}
                    <span className="contador">{fotosMaisRecentes.length}</span>
                  </h3>
                  <div className="fotos-recentes-grid">
                    {fotosMaisRecentes.map(foto => (
                      <div className="foto-recente-card" key={foto.id}>
                        <div className="foto-recente-imagem">
                          <img 
                            src={foto.url} 
                            alt={foto.dados.titulo || 'Foto sem título'} 
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/placeholder-image.jpg';
                            }}
                          />
                        </div>
                        <div className="foto-recente-info">
                          <h4>{foto.dados.titulo || 'Sem título'}</h4>
                          <p className="foto-recente-data">
                            {new Date(foto.data).toLocaleDateString('pt-BR')} 
                            {' '}{new Date(foto.data).toLocaleTimeString('pt-BR', {hour: '2-digit', minute:'2-digit'})}
                          </p>
                          {foto.dados.local && <p className="foto-recente-local">Local: {foto.dados.local}</p>}
                        </div>
                      </div>
                    ))}
                    
                    {!dataSelecionada && registros.fotos && registros.fotos.length > 4 && (
                      <div className="foto-recente-ver-mais">
                        <div className="ver-mais-circulo">
                          <span>+{registros.fotos.length - 4}</span>
                        </div>
                        <p>Mais fotos disponíveis</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Seção de registros de clima */}
              {registrosFiltrados.clima && registrosFiltrados.clima.length > 0 && (
                <div className="secao-registros">
                  <h3>
                    <i className="fas fa-cloud-sun"></i> Clima
                    <span className="contador">{registrosFiltrados.clima.length}</span>
                  </h3>
                  <div className="cards-grid">
                    {registrosFiltrados.clima.map(registro => (
                      <div className="registro-card" key={registro.id}>
                        <div className="registro-header">
                          <span className="registro-data">
                            {new Date(registro.data).toLocaleDateString('pt-BR')}
                          </span>
                          <span className="registro-hora">
                            {new Date(registro.data).toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'})}
                          </span>
                        </div>
                        <div className="registro-conteudo">
                          <p><strong>Condição:</strong> {registro.dados.condicao || ''}</p>
                          <p><strong>Temperatura:</strong> {registro.dados.temperatura || ''}°C</p>
                          <p><strong>Praticabilidade:</strong> {registro.dados.praticabilidade || ''}</p>
                          <p><strong>Período:</strong> {registro.dados.periodo || ''}</p>
                          <p><strong>Observações:</strong> {registro.dados.observacoes || ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Seção de registros de mão de obra */}
              {registrosFiltrados.maoDeObra && registrosFiltrados.maoDeObra.length > 0 && (
                <div className="secao-registros">
                  <h3>
                    <i className="fas fa-hard-hat"></i> Mão de Obra
                    <span className="contador">{registrosFiltrados.maoDeObra.length}</span>
                  </h3>
                  <div className="cards-grid">
                    {registrosFiltrados.maoDeObra.map(registro => (
                      <div className="registro-card" key={registro.id}>
                        <div className="registro-header">
                          <span className="registro-data">
                            {new Date(registro.data).toLocaleDateString('pt-BR')}
                          </span>
                          <span className="registro-hora">
                            {new Date(registro.data).toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'})}
                          </span>
                        </div>
                        <div className="registro-conteudo">
                          <p><strong>Tipo de Contratação:</strong> {registro.dados.tipoContratacao === 'terceirizado' ? 'Terceirizado' : 'Contratação Direta'}</p>
                          {registro.dados.tipoContratacao === 'terceirizado' && registro.dados.empresa && (
                            <p><strong>Empresa:</strong> {registro.dados.empresa}</p>
                          )}
                          <p><strong>Função:</strong> {registro.dados.funcao || ''}</p>
                          <p><strong>Quantidade:</strong> {registro.dados.quantidade || ''} pessoas</p>
                          {registro.dados.funcionarios && registro.dados.funcionarios.length > 0 && (
                            <div>
                              <p><strong>Funcionários:</strong></p>
                              <ul className="funcionarios-lista">
                                {registro.dados.funcionarios.map((funcionario, idx) => (
                                  <li key={idx}>{funcionario}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {registro.dados.observacoes && (
                            <p><strong>Observações:</strong> {registro.dados.observacoes}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Seção de registros de equipamentos */}
              {registrosFiltrados.equipamentos && registrosFiltrados.equipamentos.length > 0 && (
                <div className="secao-registros">
                  <h3>
                    <i className="fas fa-tools"></i> Equipamentos
                    <span className="contador">{registrosFiltrados.equipamentos.length}</span>
                  </h3>
                  <div className="cards-grid">
                    {registrosFiltrados.equipamentos.map(registro => (
                      <div className="registro-card" key={registro.id}>
                        <div className="registro-header">
                          <span className="registro-data">
                            {new Date(registro.data).toLocaleDateString('pt-BR')}
                          </span>
                          <span className="registro-hora">
                            {new Date(registro.data).toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'})}
                          </span>
                        </div>
                        <div className="registro-conteudo">
                          <h4 className="equipamento-nome">{registro.dados.nomeEquipamento || registro.dados.equipamento || 'Equipamento'}</h4>
                          <p><strong>Quantidade:</strong> {registro.dados.quantidade || ''}</p>
                          <p><strong>Horas Utilizadas:</strong> {registro.dados.horasUtilizadas || ''}</p>
                          {registro.dados.fornecedor && (
                            <p><strong>Fornecedor:</strong> {registro.dados.fornecedor}</p>
                          )}
                          {registro.dados.observacao && (
                            <p><strong>Observações:</strong> {registro.dados.observacao}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Seção de registros de materiais */}
              {registrosFiltrados.materiais && registrosFiltrados.materiais.length > 0 && (
                <div className="secao-registros">
                  <h3>
                    <i className="fas fa-box-open"></i> Materiais
                    <span className="contador">{registrosFiltrados.materiais.length}</span>
                  </h3>
                  <div className="cards-grid">
                    {registrosFiltrados.materiais.map(registro => (
                      <div className="registro-card" key={registro.id}>
                        <div className="registro-header">
                          <span className="registro-data">
                            {new Date(registro.data).toLocaleDateString('pt-BR')}
                          </span>
                          <span className="registro-hora">
                            {new Date(registro.data).toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'})}
                          </span>
                        </div>
                        <div className="registro-conteudo">
                          <p><strong>Material:</strong> {registro.dados.material || ''}</p>
                          <p><strong>Quantidade:</strong> {registro.dados.quantidade || ''} {registro.dados.unidade || ''}</p>
                          <p><strong>Fornecedor:</strong> {registro.dados.fornecedor || ''}</p>
                          <p><strong>Observações:</strong> {registro.dados.observacoes || ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Seção de registros de atividades */}
              {registrosFiltrados.atividade && registrosFiltrados.atividade.length > 0 && (
                <div className="secao-registros">
                  <h3>
                    <i className="fas fa-tasks"></i> Atividades
                    <span className="contador">{registrosFiltrados.atividade.length}</span>
                  </h3>
                  <div className="cards-grid">
                    {registrosFiltrados.atividade.map(registro => (
                      <div className="registro-card" key={registro.id}>
                        <div className="registro-header">
                          <span className="registro-data">
                            {new Date(registro.data).toLocaleDateString('pt-BR')}
                          </span>
                          <span className="registro-hora">
                            {new Date(registro.data).toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'})}
                          </span>
                        </div>
                        <div className="registro-conteudo">
                          <p><strong>Atividade:</strong> {registro.dados.descricao || ''}</p>
                          <p><strong>Local:</strong> {registro.dados.local || ''}</p>
                          <p><strong>Responsável:</strong> {registro.dados.responsavel || ''}</p>
                          <p><strong>Status:</strong> 
                            <span className={`status-badge status-${registro.dados.status?.toLowerCase() || 'em-andamento'}`}>
                              {registro.dados.status || 'Em andamento'}
                            </span>
                          </p>
                          <p><strong>Observações:</strong> {registro.dados.observacoes || ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Seção de registros de ocorrências */}
              {registrosFiltrados.ocorrencia && registrosFiltrados.ocorrencia.length > 0 && (
                <div className="secao-registros">
                  <h3>
                    <i className="fas fa-exclamation-triangle"></i> Ocorrências
                    <span className="contador">{registrosFiltrados.ocorrencia.length}</span>
                  </h3>
                  <div className="cards-grid">
                    {registrosFiltrados.ocorrencia.map(registro => (
                      <div className="registro-card" key={registro.id}>
                        <div className="registro-header">
                          <span className="registro-data">
                            {new Date(registro.data).toLocaleDateString('pt-BR')}
                          </span>
                          <span className="registro-hora">
                            {new Date(registro.data).toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'})}
                          </span>
                        </div>
                        <div className="registro-conteudo">
                          <p><strong>Ocorrência:</strong> {registro.dados.ocorrencia || ''}</p>
                          <p><strong>Observação:</strong> {registro.dados.observacao || ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Exibir qualquer outro tipo de registro que possa existir dinamicamente */}
              {Object.entries(registrosFiltrados).map(([tipo, lista]) => {
                // Pular tipos que já foram renderizados acima
                const tiposJaExibidos = ['info', 'fotos', 'clima', 'maoDeObra', 'equipamentos', 'materiais', 
                                        'atividade', 'ocorrencia', 'visita', 'nota', 'medicao'];
                
                if (tiposJaExibidos.includes(tipo) || !Array.isArray(lista) || lista.length === 0) {
                  return null;
                }
                
                return (
                  <div className="secao-registros" key={tipo}>
                    <h3>
                      <i className="fas fa-clipboard-list"></i> {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                      <span className="contador">{lista.length}</span>
                    </h3>
                    <div className="cards-grid">
                      {lista.map(registro => (
                        <div className="registro-card" key={registro.id}>
                          <div className="registro-header">
                            <span className="registro-data">
                              {new Date(registro.data).toLocaleDateString('pt-BR')}
                            </span>
                            <span className="registro-hora">
                              {new Date(registro.data).toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'})}
                            </span>
                          </div>
                          <div className="registro-conteudo">
                            {Object.entries(registro.dados || {}).map(([campo, valor]) => (
                              <p key={campo}><strong>{campo.charAt(0).toUpperCase() + campo.slice(1)}:</strong> {valor}</p>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              </div>
            )}
        </div>

        
        {/* Menu flutuante para adicionar registros - apenas para não-clientes */}
        {!isClient && (
          <FloatingMenu 
            obraId={id}
            atualizarRegistros={atualizarRegistros}
            diaAprovado={diaAprovado}
          />
        )}
      </div>

      {canManageObra && (
        <>
          <CadastroClienteModal 
            isOpen={isCadastroClienteModalOpen}
            onClose={() => setIsCadastroClienteModalOpen(false)}
            idObra={id}
            userEmpresa={currentUser?.empresa}
          />

          <CompartilharObraModal
            isOpen={isCompartilharModalOpen}
            onClose={() => setIsCompartilharModalOpen(false)}
            idObra={id}
            obraAtual={dadosObra}
          />
        </>
      )}
    </>
  );
};

export default DiarioObraDetalhesHome; 